import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, FileText, Clock, AlertCircle, Megaphone, X, CalendarDays, ChevronRight, ClipboardList, Wallet, Settings, CheckCircle, Timer, FileCheck, AlertTriangle, Calendar, Package, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import WeeklyCalendarWidget from '../components/WeeklyCalendarWidget';

const API_URL = '';

export default function Dashboard({ user, onLogout, theme, onThemeToggle }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [leaveCredits, setLeaveCredits] = useState(null);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [urgentBilling, setUrgentBilling] = useState({ count: 0, items: [] });
  const [showDialog, setShowDialog] = useState(false);
  const [showUrgentBillingDialog, setShowUrgentBillingDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', message: '', priority: 'normal' });

  // Check if user is a manager/admin who can approve items
  const isManager = ['admin', 'principal', 'hr', 'project_manager', 'design_lead'].includes(user.role);
  const canManageAnnouncements = ['admin', 'principal'].includes(user.role);
  const isPrincipal = ['admin', 'principal', 'hr'].includes(user.role);
  const isDesignUser = user.role === 'design' || user.role === 'design_lead' || isPrincipal;
  const canAccessAccounting = user.role === 'principal';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    axios.get(`${API_URL}/api/dashboard/stats`).then(res => setStats(res.data)).catch(err => console.error(err));
    axios.get(`${API_URL}/api/announcements`).then(res => setAnnouncements(res.data)).catch(err => console.error(err));
    axios.get(`${API_URL}/api/leave-credits`).then(res => setLeaveCredits(res.data)).catch(err => console.error(err));
    // Fetch pending design reviews count
    if (isDesignUser) {
      axios.get(`${API_URL}/api/design-review/pending-count`).then(res => setPendingReviews(res.data.count)).catch(err => console.error(err));
    }
    // Fetch urgent billing items for accounting users
    if (canAccessAccounting) {
      axios.get(`${API_URL}/api/billing/urgent`).then(res => setUrgentBilling(res.data)).catch(err => console.error(err));
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      const formData = new URLSearchParams();
      formData.append('title', announcementForm.title);
      formData.append('message', announcementForm.message);
      formData.append('priority', announcementForm.priority);
      await axios.post(`${API_URL}/api/announcements`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('Announcement created!');
      setAnnouncementForm({ title: '', message: '', priority: 'normal' });
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`);
      toast.success('Announcement deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20 text-red-500 border-red-500/40';
      case 'high': return 'bg-orange-500/20 text-orange-500 border-orange-500/40';
      case 'normal': return 'bg-blue-500/20 text-blue-500 border-blue-500/40';
      case 'low': return 'bg-gray-500/20 text-gray-500 border-gray-500/40';
      default: return 'bg-blue-500/20 text-blue-500 border-blue-500/40';
    }
  };

  // Stat cards - show pending items that need action (clickable for all users)
  const statCards = [
    { 
      label: 'Pending Leaves', 
      value: stats?.pending_leaves || 0, 
      icon: Clock, 
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      path: '/admin',
      tab: 'leave',
      actionText: isManager ? 'Review' : 'View'
    },
    { 
      label: 'Pending OT', 
      value: stats?.pending_ot || 0, 
      icon: Timer, 
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      path: '/admin',
      tab: 'ot',
      actionText: isManager ? 'Review' : 'View'
    },
    { 
      label: 'Open RFIs', 
      value: stats?.open_rfis || 0, 
      icon: AlertCircle, 
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      path: '/design',
      tab: 'rfi',
      actionText: 'Respond'
    },
  ];
  
  // Add Design Review card for design users
  if (isDesignUser) {
    statCards.push({
      label: isPrincipal ? 'Design Reviews' : 'My Reviews',
      value: pendingReviews,
      icon: FileCheck,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      path: '/design',
      tab: 'design-review',
      actionText: isPrincipal ? 'Review' : 'View'
    });
  }
  
  // Add Urgent Billing card for accounting users
  if (canAccessAccounting) {
    statCards.push({
      label: 'Urgent Billing',
      value: urgentBilling.count,
      icon: AlertTriangle,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      path: '/accounting',
      actionText: 'View',
      customAction: () => setShowUrgentBillingDialog(true)
    });
  }

  // Supply Chain tiles — visible to Principal, Admin, Construction
  if (user?.role === 'principal' || user?.role === 'admin' || user?.role === 'construction' || user?.department === 'Construction') {
    statCards.push({
      label: 'PO For Approval',
      value: stats?.po_for_approval || 0,
      icon: Package,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      path: '/supply-chain',
      tab: 'procurement',
      actionText: 'Review'
    });
    statCards.push({
      label: 'PO For Payment',
      value: stats?.po_for_payment || 0,
      icon: CreditCard,
      color: 'text-blue-600',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      path: '/supply-chain',
      tab: 'procurement',
      actionText: 'View'
    });
  }

  // Quick actions based on user role and department
  const getQuickActions = () => {
    const actions = [];
    
    // Common actions for all employees
    actions.push({
      label: 'Submit Leave Application',
      icon: CalendarDays,
      path: '/employee-hub',
      color: 'text-blue-500'
    });
    actions.push({
      label: 'File Overtime Request',
      icon: Timer,
      path: '/employee-hub',
      color: 'text-purple-500'
    });
    
    // Department-specific actions
    if (user.department === 'construction' || user.role === 'construction') {
      actions.push({
        label: 'Log DTR Entry',
        icon: Clock,
        path: '/construction',
        color: 'text-accent'
      });
      actions.push({
        label: 'Submit Daily Progress',
        icon: ClipboardList,
        path: '/construction',
        color: 'text-green-500'
      });
    } else if (user.department === 'design' || user.role === 'design' || user.role === 'design_lead') {
      actions.push({
        label: 'Log DTR Entry',
        icon: Clock,
        path: '/design',
        color: 'text-primary'
      });
      actions.push({
        label: 'Submit Design Review',
        icon: FileCheck,
        path: '/design',
        state: { activeTab: 'design-review' },
        color: 'text-cyan-500'
      });
      actions.push({
        label: 'Respond to RFI',
        icon: FileText,
        path: '/design',
        color: 'text-cyan-500'
      });
    }
    
    // Principal/Admin can review design submissions
    if (isPrincipal && pendingReviews > 0) {
      actions.unshift({
        label: `Review Design Submissions (${pendingReviews})`,
        icon: FileCheck,
        path: '/design',
        state: { activeTab: 'design-review' },
        color: 'text-cyan-500'
      });
    }
    
    // Admin/Manager actions
    if (isManager) {
      actions.push({
        label: 'Review Pending Approvals',
        icon: CheckCircle,
        path: '/admin',
        color: 'text-emerald-500'
      });
    }
    
    // Pay Vault for all
    actions.push({
      label: 'View Pay Vault',
      icon: Wallet,
      path: '/employee-hub',
      color: 'text-emerald-500'
    });
    
    return actions.slice(0, 6); // Max 6 actions
  };

  const quickActions = getQuickActions();

  // Handler for calendar widget navigation
  const handleCalendarNavigate = (path, tab) => {
    navigate(path, { state: { activeTab: tab } });
  };

  return (
    <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 lg:space-y-8" data-testid="dashboard-page">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">Welcome, {user.full_name}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Your centralized hub for all project activities</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-2 md:col-span-8 md:row-span-2 glass-effect rounded-lg p-4 sm:p-6" data-testid="announcements-card">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-3">
                <Megaphone className="w-6 h-6 text-primary" />
                <h2 className="text-lg sm:text-2xl font-bold text-foreground">Company Announcements</h2>
              </div>
              {canManageAnnouncements && (
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="btn-new-announcement">New Announcement</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle>Create Announcement</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateAnnouncement} className="space-y-4">
                      <div>
                        <Label htmlFor="title">Title</Label>
                        <Input
                          id="title"
                          data-testid="input-announcement-title"
                          value={announcementForm.title}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                          placeholder="Important update..."
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          data-testid="input-announcement-message"
                          value={announcementForm.message}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                          placeholder="Announcement details..."
                          rows={4}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="priority">Priority</Label>
                        <select
                          id="priority"
                          data-testid="select-announcement-priority"
                          className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                          value={announcementForm.priority}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, priority: e.target.value })}
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <Button type="submit" className="w-full" data-testid="btn-submit-announcement">
                        Post Announcement
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {announcements.length > 0 ? (
                announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className={`p-4 rounded-md border ${getPriorityColor(announcement.priority)} hover-lift`}
                    data-testid={`announcement-${announcement.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">{announcement.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded uppercase ${getPriorityColor(announcement.priority)}`}>
                            {announcement.priority}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{announcement.message}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(announcement.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {canManageAnnouncements && (
                        <button
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="ml-2 p-1 hover:bg-muted/50 rounded"
                          data-testid={`btn-delete-announcement-${announcement.id}`}
                        >
                          <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No announcements yet</p>
                  {canManageAnnouncements && <p className="text-sm">Click "New Announcement" to post one</p>}
                </div>
              )}
            </div>
          </div>

          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            const hasItems = stat.value > 0;
            return (
              <div
                key={idx}
                onClick={() => stat.customAction ? stat.customAction() : navigate(stat.path, { state: { activeTab: stat.tab } })}
                className={`col-span-1 md:col-span-4 glass-effect rounded-lg p-4 sm:p-6 hover-lift transition-all cursor-pointer hover:border-primary/50 ${hasItems ? stat.borderColor : ''}`}
                data-testid={`stat-card-${idx}`}
              >
                <div className="flex items-center justify-between mb-2 sm:mb-4">
                  <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${stat.color}`} />
                  <div className={`text-xl sm:text-3xl font-bold ${stat.color}`}>{stat.value}</div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-muted-foreground">{stat.label}</span>
                  <span className={`text-xs ${stat.color} flex items-center gap-1`}>
                    {stat.actionText} <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Weekly Calendar Widget - Before Leave Credits */}
        <WeeklyCalendarWidget user={user} onNavigate={handleCalendarNavigate} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {leaveCredits && (
            <Card className="glass-effect border-border/40" data-testid="leave-credits-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-5 h-5 text-blue-500" />
                  <CardTitle>My Leave Credits ({leaveCredits.year})</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="text-center p-3 sm:p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-xl sm:text-2xl font-bold text-blue-500">{leaveCredits.vacation_remaining?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">Vacation Leave</p>
                    <p className="text-xs text-blue-400">{leaveCredits.vacation_used || 0} used / {leaveCredits.vacation_leave || 15}</p>
                  </div>
                  <div className="text-center p-3 sm:p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-xl sm:text-2xl font-bold text-green-500">{leaveCredits.sick_remaining?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">Sick Leave</p>
                    <p className="text-xs text-green-400">{leaveCredits.sick_used || 0} used / {leaveCredits.sick_leave || 10}</p>
                  </div>
                  <div className="text-center p-3 sm:p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                    <p className="text-xl sm:text-2xl font-bold text-orange-500">{leaveCredits.emergency_remaining?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">Emergency Leave</p>
                    <p className="text-xs text-orange-400">{leaveCredits.emergency_used || 0} used / {leaveCredits.emergency_leave || 5}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="glass-effect border-border/40" data-testid="quick-actions-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {quickActions.map((action, idx) => {
                const Icon = action.icon;
                return (
                  <button 
                    key={idx}
                    onClick={() => navigate(action.path, { state: action.state })}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/30 hover:bg-muted/60 rounded-md transition-colors text-sm group"
                    data-testid={`quick-action-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-4 h-4 ${action.color}`} />
                      <span className="text-foreground">{action.label}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {isManager && (stats?.pending_leaves > 0 || stats?.pending_ot > 0) && (
            <Card className="glass-effect border-border/40 border-l-4 border-l-yellow-500" data-testid="pending-approvals-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  <CardTitle className="text-base sm:text-lg">Items Awaiting Your Approval</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {stats?.pending_leaves > 0 && (
                  <button 
                    onClick={() => navigate('/admin', { state: { activeTab: 'leave' } })}
                    className="w-full flex items-center justify-between px-3 py-3 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-md transition-colors group"
                    data-testid="pending-leaves-action"
                  >
                    <div className="flex items-center gap-3">
                      <CalendarDays className="w-5 h-5 text-orange-500" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-foreground">{stats.pending_leaves} Leave Application{stats.pending_leaves > 1 ? 's' : ''}</p>
                        <p className="text-xs text-muted-foreground">Waiting for approval</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-orange-500 font-medium">Review</span>
                      <ChevronRight className="w-4 h-4 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                )}
                {stats?.pending_ot > 0 && (
                  <button 
                    onClick={() => navigate('/admin', { state: { activeTab: 'ot' } })}
                    className="w-full flex items-center justify-between px-3 py-3 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-md transition-colors group"
                    data-testid="pending-ot-action"
                  >
                    <div className="flex items-center gap-3">
                      <Timer className="w-5 h-5 text-purple-500" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-foreground">{stats.pending_ot} OT Request{stats.pending_ot > 1 ? 's' : ''}</p>
                        <p className="text-xs text-muted-foreground">Waiting for approval</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-purple-500 font-medium">Review</span>
                      <ChevronRight className="w-4 h-4 text-purple-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                )}
                {stats?.open_rfis > 0 && (
                  <button 
                    onClick={() => navigate('/design', { state: { activeTab: 'rfi' } })}
                    className="w-full flex items-center justify-between px-3 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-md transition-colors group"
                    data-testid="open-rfis-action"
                  >
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-foreground">{stats.open_rfis} Open RFI{stats.open_rfis > 1 ? 's' : ''}</p>
                        <p className="text-xs text-muted-foreground">Require response</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-500 font-medium">Respond</span>
                      <ChevronRight className="w-4 h-4 text-red-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="glass-effect border-border/40" data-testid="recent-activity-card">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-muted-foreground">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span>Portal access granted</span>
                </div>
                <div className="text-xs text-muted-foreground ml-4">Today at {new Date().toLocaleTimeString()}</div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Urgent Billing Dialog */}
        <Dialog open={showUrgentBillingDialog} onOpenChange={setShowUrgentBillingDialog}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Urgent Billing Items ({urgentBilling.count})
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {urgentBilling.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No urgent billing items</p>
                </div>
              ) : (
                urgentBilling.items.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setShowUrgentBillingDialog(false);
                      navigate('/accounting', { state: { selectedProjectId: item.project_id } });
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg transition-colors group text-left"
                    data-testid={`urgent-billing-item-${idx}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{item.project_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${item.category === 'Design' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                          {item.category}
                        </span>
                        <span className="text-sm text-muted-foreground">{item.phase_name}</span>
                        <span className="text-xs text-muted-foreground">({item.percentage}%)</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm font-mono text-amber-500">
                          ₱{item.amount?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded bg-muted`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1 transition-transform" />
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}